import { CompactText } from 'ui/CompactText';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';

import {
  usePassportOcrPreview,
  usePassportVerificationStatus,
  useResubmitPassportPhotos,
  useSubmitPassportVerify,
  type PassportVerificationStatus,
} from 'hooks/usePassportVerification';
import { getApiErrorMessage } from 'utils/getApiErrorMessage';

import { CaptureStep } from './steps/CaptureStep';
import { ReviewStep } from './steps/ReviewStep';
import { SubmitResultStep } from './steps/SubmitResultStep';
import {
  CAPTURE_STEPS,
  type CapturedImage,
  type CaptureStepId,
} from './types';

import page from './PassportPage.module.scss';

type WizardView = CaptureStepId | 'review' | 'result';

type PrimaryCheckStatus = 'checking' | 'passed' | 'failed';

type PrimaryCheck = {
  status: PrimaryCheckStatus;
  message?: string;
};

type PrimaryChecks = Partial<Record<CaptureStepId, PrimaryCheck>>;

const STEP_TO_PHOTO_NUMBER: Record<CaptureStepId, 1 | 2 | 3> = {
  spread: 1,
  registration: 2,
  selfie: 3,
};

const PHOTO_NUMBER_TO_STEP: Record<number, CaptureStepId> = {
  1: 'spread',
  2: 'registration',
  3: 'selfie',
};

function rejectedStepsFromStatus(
  status: PassportVerificationStatus | null | undefined,
): CaptureStepId[] {
  if (!status?.photos?.length) {
    return [];
  }
  return status.photos
    .filter((photo) => photo.status === 'rejected')
    .map((photo) => PHOTO_NUMBER_TO_STEP[photo.number])
    .filter((step): step is CaptureStepId => Boolean(step));
}

export const PassportCaptureWizard = () => {
  const navigate = useNavigate();
  const [currentView, setCurrentView] = useState<WizardView>('spread');
  const [capturedImages, setCapturedImages] = useState<
    Partial<Record<CaptureStepId, CapturedImage>>
  >({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitResult, setSubmitResult] = useState<PassportVerificationStatus | null>(null);
  const [primaryChecks, setPrimaryChecks] = useState<PrimaryChecks>({});
  const [retakeTarget, setRetakeTarget] = useState<CaptureStepId | null>(null);

  const { data: existingStatus, isLoading: isStatusLoading } = usePassportVerificationStatus();
  const { mutateAsync: submitPassport, isPending: isSubmitting } = useSubmitPassportVerify();
  const { mutateAsync: resubmitPassport, isPending: isResubmitting } = useResubmitPassportPhotos();
  const { mutateAsync: previewOcr } = usePassportOcrPreview();

  const rejectedSteps = useMemo(
    () => rejectedStepsFromStatus(existingStatus),
    [existingStatus],
  );

  const isPartialResubmit =
    existingStatus?.status === 'pending_review' && rejectedSteps.length > 0;

  const liveStatus = submitResult ?? existingStatus;
  const reshootSteps = useMemo(() => {
    const photos = liveStatus?.reshoot?.required ? liveStatus.reshoot.photos ?? [] : [];
    return photos
      .map((photo) => photo.step)
      .filter((step): step is CaptureStepId =>
        step === 'spread' || step === 'registration' || step === 'selfie',
      );
  }, [liveStatus]);

  const stepsInFlow = useMemo(() => {
    if (reshootSteps.length > 0) return reshootSteps;
    if (isPartialResubmit) return rejectedSteps;
    return CAPTURE_STEPS.map((s) => s.id);
  }, [isPartialResubmit, rejectedSteps, reshootSteps]);

  useEffect(() => {
    if (stepsInFlow.length === 0 || (!isPartialResubmit && reshootSteps.length === 0)) {
      return;
    }
    setCurrentView((prev) => {
      if (prev === 'result') {
        return prev;
      }
      if (stepsInFlow.includes(prev as CaptureStepId)) {
        return prev;
      }
      return stepsInFlow[0];
    });
  }, [isPartialResubmit, reshootSteps.length, stepsInFlow]);

  const currentStepIndex = stepsInFlow.findIndex((stepId) => stepId === currentView);

  const goToNextStep = useCallback(
    (stepId: CaptureStepId) => {
      const index = stepsInFlow.findIndex((id) => id === stepId);
      const nextStep = stepsInFlow[index + 1];

      if (nextStep) {
        setCurrentView(nextStep);
      } else {
        setCurrentView('review');
      }
    },
    [stepsInFlow],
  );

  const handleConfirmCapture = useCallback(
    (image: CapturedImage) => {
      setCapturedImages((prev) => ({ ...prev, [image.id]: image }));
      const photoNumber = STEP_TO_PHOTO_NUMBER[image.id];
      if (photoNumber <= 2) {
        setPrimaryChecks((prev) => ({
          ...prev,
          [image.id]: { status: 'checking', message: 'Проверяем читаемость документа...' },
        }));
        void previewOcr({ photo: image.blob, photoNumber })
          .then((result) => {
            // p7: если сервис проверки недоступен, не блокируем отправку - фото проверит модератор вручную.
            if (result.available === false) {
              setPrimaryChecks((prev) => ({
                ...prev,
                [image.id]: {
                  status: 'passed',
                  message: 'Автопроверка сейчас недоступна - фото проверит модератор.',
                },
              }));
              return;
            }
            const passed = result.readable !== false;
            setPrimaryChecks((prev) => ({
              ...prev,
              [image.id]: {
                status: passed ? 'passed' : 'failed',
                message: passed
                  ? result.message || 'Документ читается'
                  : result.message || 'Документ не читается. Переснимите фото.',
              },
            }));
          })
          .catch((error) => {
            const message = getApiErrorMessage(
              error,
              'Не удалось выполнить первичную проверку читаемости. Переснимите фото и попробуйте снова.',
            );
            setPrimaryChecks((prev) => ({
              ...prev,
              [image.id]: { status: 'failed', message },
            }));
          });
      } else {
        setPrimaryChecks((prev) => ({
          ...prev,
          [image.id]: { status: 'passed', message: 'Фото принято' },
        }));
      }

      if (retakeTarget === image.id) {
        setRetakeTarget(null);
        setCurrentView('review');
        return;
      }
      goToNextStep(image.id);
    },
    [goToNextStep, previewOcr, retakeTarget],
  );

  const handleBackFromCapture = useCallback(() => {
    if (currentStepIndex <= 0) {
      navigate('/teacher');
      return;
    }
    setCurrentView(stepsInFlow[currentStepIndex - 1]);
  }, [currentStepIndex, navigate, stepsInFlow]);

  const handleRetake = useCallback((stepId: CaptureStepId) => {
    setSubmitError(null);
    setCapturedImages((prev) => {
      const next = { ...prev };
      delete next[stepId];
      return next;
    });
    setPrimaryChecks((prev) => {
      const next = { ...prev };
      delete next[stepId];
      return next;
    });
    setRetakeTarget(stepId);
    setCurrentView(stepId);
  }, []);

  const handleBackFromReview = useCallback(() => {
    setSubmitError(null);
    setCurrentView(stepsInFlow[stepsInFlow.length - 1] ?? 'spread');
  }, [stepsInFlow]);

  const handleSubmit = useCallback(async () => {
    setSubmitError(null);

    const requiredSteps = stepsInFlow;
    const missing = requiredSteps.filter((stepId) => !capturedImages[stepId]?.blob);
    if (missing.length > 0) {
      setSubmitError('Сделайте снимки для всех шагов перед отправкой.');
      return;
    }

    const payload = requiredSteps.reduce<Partial<Record<CaptureStepId, Blob>>>((acc, stepId) => {
      const blob = capturedImages[stepId]?.blob;
      if (blob) {
        acc[stepId] = blob;
      }
      return acc;
    }, {});

    try {
      const result =
        isPartialResubmit || reshootSteps.length > 0
          ? await resubmitPassport(payload)
          : await submitPassport(payload as Required<Record<CaptureStepId, Blob>>);
      setSubmitResult(result);
      const reshootStep = result.reshoot?.required ? result.reshoot.photos?.[0]?.step : undefined;
      if (reshootStep) {
        setSubmitError(null);
        setCurrentView(reshootStep);
        return;
      }
      setCurrentView('result');
      setCapturedImages({});
      setPrimaryChecks({});
    } catch (error) {
      setSubmitError(
        getApiErrorMessage(
          error,
          'Не удалось отправить документы. Проверьте подключение и попробуйте снова.',
        ),
      );
    }
  }, [
    capturedImages,
    isPartialResubmit,
    primaryChecks,
    reshootSteps.length,
    resubmitPassport,
    stepsInFlow,
    submitPassport,
  ]);

  const resultData = submitResult ?? existingStatus;

  const showPendingResult =
    resultData &&
    !resultData.reshoot?.required &&
    !isPartialResubmit &&
    resultData.status === 'pending_review' &&
    rejectedSteps.length === 0 &&
    (currentView === 'result' ||
      (currentView === 'spread' && !capturedImages.spread));

  const showApprovedResult =
    resultData?.status === 'approved' && currentView !== 'review';

  // p8: пока статус не загружен, не показываем форму загрузки - иначе у уже подтверждённого
  // паспорта на секунду появляется «Шаг 1 из 3».
  if (isStatusLoading && !submitResult) {
    return (
      <CompactText className={page.hint} role='status'>
        Загружаем статус проверки документов...
      </CompactText>
    );
  }

  if (showPendingResult || showApprovedResult) {
    return <SubmitResultStep result={resultData} />;
  }

  if (currentView === 'review') {
    return (
      <>
        <ReviewStep
          images={capturedImages}
          onRetake={handleRetake}
          onSubmit={handleSubmit}
          onBack={handleBackFromReview}
          isSubmitting={isSubmitting || isResubmitting}
          errorMessage={submitError}
          primaryChecks={primaryChecks}
          stepsToShow={stepsInFlow}
          submitLabel={isPartialResubmit ? 'Отправить исправленные фото' : 'Отправить'}
          title={isPartialResubmit ? 'Пересдача отклоненных фото' : 'Проверка фото'}
          subtitle={
            isPartialResubmit
              ? 'Переснимите только те снимки, которые отклонил модератор'
              : 'Убедитесь, что все снимки четкие и данные читаются'
          }
        />
      </>
    );
  }

  const activeStepId = currentView as CaptureStepId;
  const activeConfig = CAPTURE_STEPS.find((step) => step.id === activeStepId);

  if (!activeConfig) {
    return null;
  }

  const rejectedPhoto = existingStatus?.photos?.find(
    (p) => p.number === STEP_TO_PHOTO_NUMBER[activeStepId] && p.status === 'rejected',
  );

  const notice =
    liveStatus?.reshoot?.required && liveStatus.reshoot.message ? (
      <CompactText className={page.hint} role='status' tone='warning'>
        {liveStatus.reshoot.message}
      </CompactText>
    ) : rejectedPhoto?.comment ? (
      // p7: причину показываем и при полном отказе (status=rejected), не только при частичной пересдаче.
      <CompactText className={page.hint} role='status' tone='error'>
        Модератор отклонил это фото. Причина: {rejectedPhoto.comment}
      </CompactText>
    ) : null;

  return (
    <CaptureStep
      key={activeConfig.id}
      config={activeConfig}
      onConfirm={handleConfirmCapture}
      onBack={handleBackFromCapture}
      active
      showBack={currentStepIndex > 0}
      notice={notice}
    />
  );
};
